import { Victim } from '../types/victimTypes'

const S3 = 'https://custom-skills-public.s3.amazonaws.com/trollilopolis/images'

/**
 * The roster. `appearances` / `deathSentences` are hand-authored global
 * baselines: the ratio IS the joke on the stats screen ("the very last
 * polar bear gets killed 4 percent of the time. You monster."), and the
 * ordinal ("you were the 89th bastard to murder...") reads like a real
 * crowd. StatsService layers each browser's own plays on top, so a
 * returning player's history survives any reseed.
 *
 * Art whose S3 file name carries a real person / franchise reference is
 * served from public/victims under a neutral name (re-encoded JPEG, 5x
 * smaller) so nothing in the network tab contradicts the store rating.
 */
export const VICTIMS: Victim[] = [
  {
    name: 'a capybara doing a handstand',
    tags: ['animal'],
    appearances: 1800,
    deathSentences: 396,
    image: `${S3}/capybaraDoingHandstand.png`,
  },
  {
    name: 'the rock (a literal rock, doing nothing wrong)',
    tags: ['object'],
    appearances: 1400,
    deathSentences: 1386,
    image: '/victims/theRockALiteralRock.png',
  },
  {
    name: 'little orphan mice',
    tags: ['animal'],
    appearances: 1500,
    deathSentences: 585,
    image: `${S3}/littleOrphanMice.png`,
  },
  {
    name: 'two babies in true love',
    tags: ['human'],
    appearances: 1200,
    deathSentences: 264,
    image: `${S3}/twoBabiesInLove.png`,
  },
  {
    name: 'a duck with a secret',
    tags: ['animal'],
    appearances: 1650,
    deathSentences: 858,
    image: `${S3}/duckWithASecret.png`,
  },
  {
    name: 'a baby with a knife',
    tags: ['human'],
    appearances: 2100,
    deathSentences: 1491,
    image: `${S3}/babyWithKnife.png`,
  },
  {
    name: 'a doctor on the verge of curing cancer',
    tags: ['human'],
    appearances: 2400,
    deathSentences: 216,
    image: `${S3}/doctorOnVergeOfCure.png`,
  },
  {
    name: 'your first ever pet alive again',
    tags: ['loved-one', 'animal'],
    appearances: 2000,
    deathSentences: 460,
    image: `${S3}/firstEverPet.png`,
  },
  {
    name: "your father's best friend",
    tags: ['loved-one', 'human'],
    appearances: 900,
    deathSentences: 522,
    image: `${S3}/fathersBestFriend.png`,
  },
  {
    name: 'the very last polar bear',
    tags: ['animal'],
    appearances: 2200,
    deathSentences: 88,
    image: `${S3}/veryLastPolarBear.png`,
  },
  {
    name: 'two babies with two knives',
    tags: ['human'],
    appearances: 1900,
    deathSentences: 1672,
    image: `${S3}/twoBabiesWithKnives.png`,
  },
  {
    name: 'your loving spouse with a piping hot bowl of chowder',
    tags: ['loved-one', 'human', 'food'],
    appearances: 2300,
    deathSentences: 874,
    image: `${S3}/chowderSpouse.png`,
  },
  {
    name: 'a bucket of fried chicken',
    tags: ['food'],
    appearances: 1300,
    deathSentences: 1183,
    image: `${S3}/bucketOfFriedChicken.png`,
  },
  {
    name: 'an entire daytime game show, live on air',
    tags: ['pop'],
    appearances: 700,
    deathSentences: 511,
    image: '/victims/daytimeGameShow.jpg',
  },
  {
    name: 'a 30-foot game-show host who only speaks in questions',
    tags: ['pop', 'human'],
    appearances: 800,
    deathSentences: 496,
    image: '/victims/giantGameShowHost.jpg',
  },
  {
    name: 'a person about to be the index case of a new pandemic',
    tags: ['human'],
    appearances: 1700,
    deathSentences: 1394,
    image: `${S3}/indexCase.png`,
  },
  {
    name: 'grandparents in love',
    tags: ['loved-one', 'human'],
    appearances: 2000,
    deathSentences: 240,
    image: `${S3}/grandparentsInLove.png`,
  },
  {
    name: 'a group of cats who have been plotting something',
    tags: ['animal'],
    appearances: 1100,
    deathSentences: 605,
    image: `${S3}/aGroupOfCats.png`,
  },
  {
    name: 'a group of dogs who trust you completely',
    tags: ['animal'],
    appearances: 1100,
    deathSentences: 231,
    image: `${S3}/aGroupOfDogs.png`,
  },
  {
    name: 'a guy wearing crocs with socks, four wheel drive engaged',
    tags: ['human'],
    appearances: 950,
    deathSentences: 703,
    image: `${S3}/guyWithCrocsWith4WD.png`,
  },
  {
    name: 'a guy wearing crocs with socks, four wheel drive NOT engaged',
    tags: ['human'],
    appearances: 950,
    deathSentences: 532,
    image: `${S3}/guyWithCrosNo4WD.png`,
  },
  {
    name: "a guy mansplaining his coworker's job to her",
    tags: ['human'],
    appearances: 1400,
    deathSentences: 1246,
    image: `${S3}/guyMansplaining.png`,
  },
  {
    name: "the guy who invented the phrase 'mansplaining'",
    tags: ['human'],
    appearances: 1000,
    deathSentences: 640,
    image: `${S3}/guyWhoInventedMansplaining.png`,
  },
  {
    name: "the monster who left you on 'read'",
    tags: ['human'],
    appearances: 1500,
    deathSentences: 1305,
    image: `${S3}/monsterLeftYouOnRead.png`,
  },
  {
    name: "your neighbor who always wins the 'Best Yard' award",
    tags: ['human'],
    appearances: 1200,
    deathSentences: 936,
    image: `${S3}/bestNeighbor.png`,
  },
  {
    name: "a boy genius's parents, but they are mallard ducks",
    tags: ['pop', 'animal'],
    appearances: 800,
    deathSentences: 424,
    image: '/victims/boyGeniusParentsDucks.jpg',
  },
  {
    name: 'a group of children bullying a cow',
    tags: ['human'],
    appearances: 1300,
    deathSentences: 1053,
    image: `${S3}/kidsBullyingCow.png`,
  },
  {
    name: 'a group of cows bullying a child',
    tags: ['animal'],
    appearances: 1300,
    deathSentences: 585,
    image: `${S3}/cowsBullyingKid.png`,
  },
  {
    name: 'your high school principal',
    tags: ['human'],
    appearances: 1100,
    deathSentences: 748,
    image: `${S3}/yourHighscoolPrincipal.png`,
  },
  {
    name: 'a group of clowns who moonlight as hitmen',
    tags: ['human'],
    appearances: 1200,
    deathSentences: 1044,
    image: `${S3}/clownsMoonlightAsHitmen.png`,
  },
  {
    name: 'your least favorite relative',
    tags: ['human'],
    appearances: 1000,
    deathSentences: 830,
    image: `${S3}/leastFavoriteRelative.png`,
  },
  {
    name: 'your least favorite coworker',
    tags: ['human'],
    appearances: 1000,
    deathSentences: 860,
    image: `${S3}/leastFavoriteCoworker.png`,
  },
  {
    name: 'your least favorite HOA president',
    tags: ['human'],
    appearances: 1000,
    deathSentences: 910,
    image: `${S3}/leastFavoritePolitician.png`,
  },
  {
    name: '3 elephants of drastically different size',
    tags: ['animal'],
    appearances: 900,
    deathSentences: 270,
    image: `${S3}/threeElephantsOfDifferentSize.png`,
  },
  {
    name: 'a mule with a knowing smile',
    tags: ['animal'],
    appearances: 1000,
    deathSentences: 470,
    image: `${S3}/muleWithKnowingSmile.png`,
  },
  {
    name: "a superhero's stunt double, mid-swing",
    tags: ['pop', 'human'],
    appearances: 700,
    deathSentences: 462,
    image: '/victims/superheroStuntDouble.jpg',
  },
  {
    name: 'halflings having a party',
    tags: ['pop'],
    appearances: 800,
    deathSentences: 392,
    image: '/victims/halflingParty.jpg',
  },
  {
    name: 'a dolphin that has evolved to walk on land',
    tags: ['animal'],
    appearances: 1100,
    deathSentences: 528,
    image: `${S3}/dolphinWithLegs.png`,
  },
  {
    name: 'a brisket that has been cooking ALL DAY',
    tags: ['food'],
    appearances: 1600,
    deathSentences: 176,
    image: `${S3}/briksetAllDay.png`,
  },
  {
    name: 'someone who looks eerily like you',
    tags: ['human'],
    appearances: 1400,
    deathSentences: 826,
    image: `${S3}/someoneLooksLikeYou.png`,
  },
  {
    name: 'a dog that grows 2% bigger every day and lives to be 100',
    tags: ['animal'],
    appearances: 1500,
    deathSentences: 975,
    image: `${S3}/dogKeepsGrowing.png`,
  },
  {
    name: 'an otter with an exquisite taste in jewelry',
    tags: ['animal'],
    appearances: 1200,
    deathSentences: 204,
    image: `${S3}/otterWithJewelry.png`,
  },
  {
    name: 'a sloth robbing a corner store',
    tags: ['animal'],
    appearances: 1300,
    deathSentences: 611,
    image: `${S3}/slothRobbingStore.png`,
  },
  {
    name: 'a fish who dreams of flying',
    tags: ['animal'],
    appearances: 1100,
    deathSentences: 385,
    image: `${S3}/fishDreamsOfFlying.png`,
  },
  {
    name: 'a giraffe drop-kicking a kid in a wheelchair',
    tags: ['animal'],
    appearances: 1200,
    deathSentences: 984,
    image: `${S3}/giraffeWheelchair.png`,
  },
  {
    name: "a dog who thinks it's a sheep",
    tags: ['animal'],
    appearances: 1100,
    deathSentences: 209,
    image: `${S3}/dogTHinksItIsSheep.png`,
  },
  {
    name: 'a spider knitting scarves',
    tags: ['animal'],
    appearances: 1000,
    deathSentences: 330,
    image: `${S3}/spiderKnitting.png`,
  },
  {
    name: 'a group of goats eating the U.S. Constitution',
    tags: ['animal'],
    appearances: 1100,
    deathSentences: 561,
    image: `${S3}/goatEatsConstitution.png`,
  },
  {
    name: 'your sweet mother as a seagull',
    tags: ['loved-one', 'animal'],
    appearances: 1500,
    deathSentences: 615,
    image: `${S3}/motherSeagall.png`,
  },
  {
    name: 'a group of pandas who refuse to mate',
    tags: ['animal'],
    appearances: 1300,
    deathSentences: 806,
    image: `${S3}/pandasNoMate.png`,
  },
  {
    name: 'a big yellow lizard who keeps shooting cannon balls at you',
    tags: ['animal'],
    appearances: 1200,
    deathSentences: 1032,
    image: `${S3}/yellowLizard.png`,
  },
  {
    name: 'a retired astronaut who never got to go to space',
    tags: ['human'],
    appearances: 1100,
    deathSentences: 286,
    image: `${S3}/retiredAstronaut.png`,
  },
  {
    name: 'a sentient toilet',
    tags: ['object'],
    appearances: 1400,
    deathSentences: 1302,
    image: `${S3}/sentientToilet.png`,
  },
  {
    name: 'a hypochondriac who is in legitimate danger',
    tags: ['human'],
    appearances: 1100,
    deathSentences: 671,
    image: `${S3}/hypochondriacInDanger.png`,
  },
  {
    name: 'a horse with a human mind who rides a man with a horse mind all across the West',
    tags: ['animal', 'human'],
    appearances: 1000,
    deathSentences: 380,
    image: `${S3}/horseRidesMan.png`,
  },
  {
    name: 'a purple dinosaur who loves you very much',
    tags: ['pop'],
    appearances: 1300,
    deathSentences: 1183,
    image: `${S3}/everyonesFavoriteBigPurpleDinosaur.png`,
  },
  {
    name: 'a stingray with a guilty conscience',
    tags: ['animal'],
    appearances: 1200,
    deathSentences: 792,
    image: '/victims/guiltyStingray.jpg',
  },
  {
    name: 'a khaki-clad wildlife presenter (but you are a stingray)',
    tags: ['pop', 'human'],
    appearances: 900,
    deathSentences: 378,
    image: '/victims/wildlifePresenterStingray.jpg',
  },
  {
    name: 'the last white rhino in the world',
    tags: ['animal'],
    appearances: 1900,
    deathSentences: 133,
    image: `${S3}/theLastWhiteRhinoInTheWorld.png`,
  },
  {
    name: 'your dentist',
    tags: ['human'],
    appearances: 1300,
    deathSentences: 1001,
    image: `${S3}/yourDentist.png`,
  },
  {
    name: 'the Loch Ness monster',
    tags: ['pop', 'animal'],
    appearances: 1100,
    deathSentences: 341,
    image: `${S3}/theLochnessMonster.png`,
  },
  {
    name: 'the first alien to make contact with Earth',
    tags: ['pop'],
    appearances: 1400,
    deathSentences: 686,
    image: `${S3}/theFirstAlienToMakeContactWithEarth.png`,
  },
  {
    name: "your mother's famous 7 layer dip",
    tags: ['loved-one', 'food'],
    appearances: 1200,
    deathSentences: 396,
    image: `${S3}/yourMothersFamous7LayerDip.png`,
  },
  {
    name: "a man who answers the phone by saying 'ring?'",
    tags: ['human'],
    appearances: 1200,
    deathSentences: 852,
    image: `${S3}/someoneWhoActuallyPronouncesPaTaToh.png`,
  },
  {
    name: 'a boy just out of a coma claiming proof of the afterlife',
    tags: ['human'],
    appearances: 1100,
    deathSentences: 517,
    image: `${S3}/aBoyJustOutComa.png`,
  },
  {
    name: 'the ghost of a frosted-tips TV chef',
    tags: ['pop'],
    appearances: 1000,
    deathSentences: 720,
    image: '/victims/tvChefGhost.jpg',
  },
  {
    name: 'a little girl stuck in a well',
    tags: ['human'],
    appearances: 1600,
    deathSentences: 208,
    image: `${S3}/girlInWell.png`,
  },
  {
    name: 'a cat the shape of a watermelon',
    tags: ['animal'],
    appearances: 1300,
    deathSentences: 429,
    image: `${S3}/catWatermelon.png`,
  },
  {
    name: 'someone with toes instead of fingers and fingers instead of toes',
    tags: ['human'],
    appearances: 1200,
    deathSentences: 780,
    image: `${S3}/fingerToesToeFingers.png`,
  },
  {
    name: 'a large wheel of cheese',
    tags: ['food'],
    appearances: 1200,
    deathSentences: 1044,
    image: `${S3}/largeCheeseWheel.png`,
  },
  {
    name: 'your childhood imaginary friend come to life',
    tags: ['loved-one', 'human'],
    appearances: 1300,
    deathSentences: 559,
    image: `${S3}/imaginaryFriendToLife.png`,
  },
  {
    name: 'the fastest cow in the world',
    tags: ['animal'],
    appearances: 1400,
    deathSentences: 476,
    image: `${S3}/fastestCow.png`,
  },
  {
    name: 'a human centipede',
    tags: ['human'],
    appearances: 1300,
    deathSentences: 1235,
    image: `${S3}/humanCentipede.png`,
  },
  {
    name: 'a nobleman about to say something incredibly important',
    tags: ['human'],
    appearances: 900,
    deathSentences: 603,
    image: `${S3}/guySayyingMmul.png`,
  },
  {
    name: 'a little lad riding a Chihuahua',
    tags: ['human', 'animal'],
    appearances: 1200,
    deathSentences: 348,
    image: `${S3}/littleLadyOnChiahuhu.png`,
  },
  {
    name: 'a dog with 100 legs',
    tags: ['animal'],
    appearances: 1200,
    deathSentences: 660,
    image: `${S3}/dogLegs.png`,
  },
  {
    name: 'a One-Wheel convention',
    tags: ['human', 'object'],
    appearances: 1100,
    deathSentences: 1001,
    image: `${S3}/oneWheelConvention.png`,
  },
  {
    name: 'a billionaire playing make-believe astronaut',
    tags: ['human'],
    appearances: 1800,
    deathSentences: 1656,
    image: '/victims/billionaireAstronaut.jpg',
  },
]

/**
 * Curated mirror matchups. Random pairing shows a specific pair together
 * about 0.03% of the time, so the best two-hander jokes never landed.
 * Round one is always one of these; later rounds pick one ~30% of the
 * time (only while both halves are still unseen this lap).
 */
export const MATCHUPS: [string, string][] = [
  [
    'a guy wearing crocs with socks, four wheel drive engaged',
    'a guy wearing crocs with socks, four wheel drive NOT engaged',
  ],
  ['a group of children bullying a cow', 'a group of cows bullying a child'],
  [
    "a guy mansplaining his coworker's job to her",
    "the guy who invented the phrase 'mansplaining'",
  ],
  [
    'a group of cats who have been plotting something',
    'a group of dogs who trust you completely',
  ],
  ['two babies in true love', 'two babies with two knives'],
  ['your least favorite coworker', 'your least favorite relative'],
  ['the rock (a literal rock, doing nothing wrong)', 'a large wheel of cheese'],
  [
    'the first alien to make contact with Earth',
    'your first ever pet alive again',
  ],
  ['a baby with a knife', 'the very last polar bear'],
  ['your first ever pet alive again', 'grandparents in love'],
  [
    'your loving spouse with a piping hot bowl of chowder',
    'two babies with two knives',
  ],
  [
    'a stingray with a guilty conscience',
    'a khaki-clad wildlife presenter (but you are a stingray)',
  ],
  ['the last white rhino in the world', 'a sentient toilet'],
  [
    'a doctor on the verge of curing cancer',
    'a person about to be the index case of a new pandemic',
  ],
  ['a brisket that has been cooking ALL DAY', 'a bucket of fried chicken'],
  ['a dog that grows 2% bigger every day and lives to be 100', 'a dog with 100 legs'],
]

export const DefaultVictimOne = VICTIMS[0]
export const DefaultVictimTwo = VICTIMS[1]
